import { Badge, BadgeType, Grid, OParagraph, OSmallParagraph } from '@lifechurch/react-ion';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';
import {
  MomentTemplateFields_NextStepsMoment_Fragment,
  MomentTemplateFieldsFragment,
  MomentType,
} from '@generated/types';
import { getTitle, getContent, getImage, getLocalizedTitle } from '@lib/moments';
import { ChopImage } from '@components/ChopImage';
import { useTranslation } from 'next-i18next';
import NextStepsDefaultIcon from './NextStepsDefaultIcon';

interface Props {
  momentTemplate: MomentTemplateFieldsFragment;
}

interface NextStepsProps {
  momentTemplate: MomentTemplateFields_NextStepsMoment_Fragment;
}

const ThumbnailImage = styled(ChopImage)`
  padding-inline-end: 8px;
`;

const Wrapper = styled(Grid)<{ gridTemplateColumns?: string }>`
  padding: ${props => props.theme.space[5]}px;
  align-items: center;
  cursor: pointer;
  grid-template-columns: ${props => props.gridTemplateColumns || '100px 1fr 85px 165px'};
  grid-column-gap: 1rem;
  position: relative;

  &:not(:last-child):after {
    position: absolute;
    bottom: 0;
    left: ${props => props.theme.space[5]}px;
    right: ${props => props.theme.space[5]}px;
    display: block;
    height: 1px;
    content: '';
    background-color: ${props => props.theme.colors.gray10};
  }

  div {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  div:not(:first-child) {
    color: ${props => props.theme.colors.gray80};
  }

  &:hover {
    background-color: ${props => props.theme.colors.action.light};

    & > div {
      color: ${props => props.theme.colors.gray100};
      fill: ${props => props.theme.colors.gray100};
    }

    & > div:first-child,
    & > div:first-child svg path {
      color: ${props => props.theme.colors.action.hover};
      fill: ${props => props.theme.colors.action.hover};
    }

    &:after {
      display: none;
    }

    &:not(:first-child) {
      box-shadow: 0px -1px ${props => props.theme.colors.action.light};
    }
  }

  svg {
    margin-inline-start: 4px;
    &:hover {
      cursor: pointer;
    }
  }
`;

const ContentWrapper = styled.div`
  display: inline-flex;
  align-items: center;
`;

const LightText = styled(OSmallParagraph)`
  color: ${props => props.theme.colors.gray50};
`;

const URLText = styled(LightText)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const searchByTitleAndContent = (
  momentTemplate: MomentTemplateFieldsFragment,
  searchText: string
): boolean => {
  const title = getTitle(momentTemplate?.type).toLowerCase();
  const content = getContent(momentTemplate)?.toLowerCase();
  return (
    title?.includes(searchText.toLowerCase()) ||
    content?.includes(searchText.toLowerCase()) ||
    false
  );
};

const TableRow: React.FC<Props> = ({ momentTemplate }) => {
  const { t } = useTranslation(['moments', 'common']);

  const router = useRouter();

  const handleClick = (): void => {
    router.push(`/moment/[id]`, `/moment/${momentTemplate.id}`);
  };

  return (
    <Wrapper onClick={handleClick}>
      <OParagraph as='div'>{getLocalizedTitle(momentTemplate.type, t)}</OParagraph>
      <ContentWrapper>
        {momentTemplate.type === MomentType.IMAGE ? (
          <ThumbnailImage height={44} src={getImage(momentTemplate).url} />
        ) : null}
        <OSmallParagraph>{getContent(momentTemplate)}</OSmallParagraph>
      </ContentWrapper>
      <OSmallParagraph>{dayjs(momentTemplate.updatedAt).format('D MMM YYYY')}</OSmallParagraph>
      <div>
        {momentTemplate.includedInFutureServices && (
          <Badge type={BadgeType.INFORMATION}>{t('include_in_new_services')}</Badge>
        )}
      </div>
    </Wrapper>
  );
};

const NextStepsTableRow: React.FC<NextStepsProps> = ({ momentTemplate }) => {
  const router = useRouter();

  const handleClick = (): void => {
    router.push(`/next_steps/[id]`, `/next_steps/${momentTemplate.id}`);
  };

  return (
    <Wrapper gridTemplateColumns='32px 160px minmax(0,2fr) minmax(0,1fr)' onClick={handleClick}>
      <ContentWrapper>
        {momentTemplate.options.iconUrl ? (
          <ChopImage height={32} src={momentTemplate.options.iconUrl} />
        ) : (
          <NextStepsDefaultIcon />
        )}
      </ContentWrapper>
      <OParagraph>{momentTemplate.options.text}</OParagraph>
      <LightText>{momentTemplate.options.description}</LightText>
      <URLText>{momentTemplate.options.url}</URLText>
    </Wrapper>
  );
};

export { TableRow, NextStepsTableRow };
